import { useCallback, useEffect, useRef } from 'react';
export function useInfiniteScroll({ hasMore = false, loadingMore = false, onLoadMore, rootMargin = '70px', threshold = 0.1, }) {
    const observerRef = useRef(null);
    const loadingRef = useRef(null);
    const handleScrollToEnd = useCallback(() => {
        if (hasMore && !loadingMore && onLoadMore) {
            onLoadMore();
        }
    }, [hasMore, loadingMore, onLoadMore]);
    useEffect(() => {
        if (!loadingRef.current)
            return;
        if (observerRef.current) {
            observerRef.current.disconnect();
        }
        observerRef.current = new IntersectionObserver(entries => {
            const entry = entries[0];
            if (entry?.isIntersecting) {
                handleScrollToEnd();
            }
        }, {
            rootMargin,
            threshold,
        });
        observerRef.current.observe(loadingRef.current);
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [handleScrollToEnd, rootMargin, threshold]);
    const triggerLoadMore = useCallback(() => {
        handleScrollToEnd();
    }, [handleScrollToEnd]);
    const disconnectObserver = useCallback(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
    }, []);
    const reconnectObserver = useCallback(() => {
        if (!loadingRef.current || observerRef.current)
            return;
        observerRef.current = new IntersectionObserver(entries => {
            const [entry] = entries;
            if (entry?.isIntersecting) {
                handleScrollToEnd();
            }
        }, {
            rootMargin,
            threshold,
        });
        observerRef.current.observe(loadingRef.current);
    }, [handleScrollToEnd, rootMargin, threshold]);
    return {
        loadingRef,
        triggerLoadMore,
        disconnectObserver,
        reconnectObserver,
        isObserverActive: observerRef.current !== null,
    };
}
